import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Medal, Shirt, Trophy, Droplets, Coffee, BedDouble, Route as RouteIcon, ChevronDown, Award, Phone, Mail, Ban, Recycle, Leaf, HeartHandshake, Compass, Lightbulb } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import RegisterModal from "@/components/RegisterModal";
import VolunteerForm from "@/components/VolunteerForm";
import Countdown from "@/components/Countdown";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-brand-line py-12 first:border-0">
      <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl">{title}</h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Faq({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="card-k overflow-hidden">
          <button data-testid={`event-faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)} className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-semibold text-white">
            {f.q}
            <ChevronDown size={16} className={`text-brand-cyan transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <p className="px-5 pb-4 text-sm text-slate-400">{f.a}</p>}
        </div>
      ))}
    </div>
  );
}

function missionIcon(text) {
  if (/drug/i.test(text)) return Ban;
  if (/plastic/i.test(text)) return Recycle;
  return Leaf;
}

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showReg, setShowReg] = useState(false);
  const [showVol, setShowVol] = useState(false);

  useEffect(() => {
    setEvent(null);
    api.get(`/events/${slug}`).then((r) => setEvent(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="mx-auto max-w-7xl px-4 py-32 text-center sm:px-6">
      <p className="eyebrow">404</p>
      <h1 className="mt-3 font-display text-4xl font-bold text-white">Event not found</h1>
      <Link to="/events" className="btn-kinetic btn-primary-k mt-8">Browse Events</Link>
    </div>
  );
  if (!event) return <div className="flex min-h-[50vh] items-center justify-center font-mono text-xs tracking-[0.3em] text-brand-cyan">LOADING EVENT...</div>;

  const canRegister = event.status === "registration_open";

  return (
    <div data-testid="event-detail-page">
      <section className="grain relative overflow-hidden">
        <img src={event.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/80 to-brand-ink/30" />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-32 sm:px-6 lg:pt-40">
          <StatusBadge status={event.status} />
          {event.fest_name && (
            <div data-testid="fest-badge" className="mt-4 inline-flex flex-wrap items-center gap-3 rounded-full border border-brand-olive/50 bg-brand-ink/70 px-5 py-2 backdrop-blur">
              {event.fest_logos?.map((l) => (
                <img key={l.label} src={l.url} alt={l.label} title={l.label} className="h-8 w-8 rounded-full border border-brand-line bg-white object-contain p-0.5" />
              ))}
              <span className="font-mono text-[10px] tracking-[0.2em] text-brand-olive">AN OFFICIAL RUN OF {event.fest_name.toUpperCase()}</span>
            </div>
          )}
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">{event.name}</h1>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs tracking-[0.15em] text-slate-300">
            <span className="flex items-center gap-2"><CalendarDays size={14} className="text-brand-olive" /> {event.date_text}</span>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-brand-cyan" /> {event.venue}</span>
          </div>
          {event.distances?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {event.distances.map((d) => (
                <span key={d} className="rounded-lg border border-brand-cyan/40 bg-brand-ink/60 px-4 py-1.5 font-mono text-sm font-semibold text-brand-cyan backdrop-blur">{d}</span>
              ))}
            </div>
          )}
          <p className="mt-6 font-mono text-xs tracking-[0.3em] text-brand-olive">RUN • EXPLORE • CONNECT</p>
          {event.event_date && ["registration_open", "coming_soon"].includes(event.status) && (
            <div className="mt-6"><Countdown date={event.event_date} /></div>
          )}
          <div className="mt-8">
            {canRegister && event.registration_mode === "onsite" && (
              <button data-testid="event-register-btn" onClick={() => setShowReg(true)} className="btn-kinetic btn-primary-k">REGISTER NOW</button>
            )}
            {canRegister && event.registration_mode === "external" && event.registration_link && (
              <a data-testid="event-register-btn" href={event.registration_link} target="_blank" rel="noreferrer" className="btn-kinetic btn-primary-k">REGISTER NOW</a>
            )}
            {!canRegister && event.status === "coming_soon" && (
              <span data-testid="event-coming-soon" className="inline-flex rounded-full border border-yellow-400/40 bg-yellow-400/10 px-6 py-3 font-mono text-xs tracking-[0.2em] text-yellow-400">STAY TUNED — REGISTRATION OPENS SOON</span>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        {event.status === "coming_soon" && (
          <div data-testid="coming-soon-banner" className="mb-10 flex flex-wrap items-center gap-4 rounded-2xl border border-yellow-400/50 bg-gradient-to-r from-yellow-400/15 via-brand-olive/10 to-transparent p-6">
            <span className="rounded-full bg-gradient-to-r from-yellow-400 to-brand-olive px-5 py-2 font-display text-lg font-bold uppercase tracking-widest text-brand-ink">Coming Soon</span>
            <div>
              <p className="font-display text-xl font-bold uppercase text-white">New Routes. New Destinations. New Adventures.</p>
              <p className="mt-1 text-sm text-slate-400">Registration for this event opens soon. Subscribe at the bottom of the page to get notified the moment it goes live.</p>
            </div>
          </div>
        )}
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Section title="About the Event">
              {event.fest_name && (
                <div data-testid="fest-mention" className="mb-5 flex flex-wrap items-center gap-4 rounded-xl border border-brand-cyan/40 bg-gradient-to-r from-brand-cyan/10 via-brand-olive/5 to-transparent p-4">
                  <div className="flex items-center gap-2">
                    {event.fest_logos?.map((l) => (
                      <img key={l.label} src={l.url} alt={l.label} title={l.label} className="h-11 w-11 rounded-full border border-brand-line bg-white object-contain p-1" />
                    ))}
                  </div>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">PROUDLY PART OF</p>
                    <p className="font-display text-lg font-bold uppercase tracking-wide text-white">{event.fest_name}</p>
                  </div>
                </div>
              )}
              <p className="leading-relaxed text-slate-300">{event.description}</p>
              {event.entry_fee && <p className="mt-4 text-sm text-slate-400">Entry fee: <span className="font-semibold text-brand-olive">{event.entry_fee}</span></p>}
              {event.social_messages?.length > 0 && (
                <div className="mt-7">
                  <p className="font-mono text-xs tracking-[0.2em] text-brand-cyan">OUR MISSION THIS EDITION</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    {event.social_messages.map((m) => {
                      const Icon = missionIcon(m);
                      return (
                        <div key={m} data-testid={`mission-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="card-k flex items-center gap-3 border-brand-olive/30 px-4 py-3.5">
                          <Icon size={18} className="shrink-0 text-brand-olive" />
                          <span className="font-display text-sm font-bold uppercase tracking-wide text-white">{m}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {event.relief_message && (
                <div data-testid="relief-callout" className="mt-5 flex items-start gap-4 rounded-xl border border-brand-cyan/40 bg-gradient-to-r from-brand-cyan/15 via-brand-olive/10 to-transparent p-5">
                  <HeartHandshake size={22} className="mt-0.5 shrink-0 text-brand-cyan" />
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide text-white">{event.relief_title || "Run For A Cause"}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-300">{event.relief_message}</p>
                  </div>
                </div>
              )}
            </Section>

            {event.destination_info && (
              <Section title="About the Destination">
                <p className="leading-relaxed text-slate-300">{event.destination_info}</p>
              </Section>
            )}

            {event.attractions?.length > 0 && (
              <Section title="Nearby Attractions">
                <p className="mb-5 font-mono text-xs tracking-[0.2em] text-brand-cyan">MAKE IT A TRIP, NOT JUST A RACE</p>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {event.attractions.map((a) => (
                    <div key={a} data-testid={`attraction-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="card-k flex items-center gap-2.5 px-4 py-3 text-sm text-slate-200">
                      <Compass size={15} className="shrink-0 text-brand-olive" /> {a}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {(event.route_info || event.route_map || event.route_loops?.length > 0) && (
              <Section title="Route & Elevation">
                {event.route_info && <p className="flex items-start gap-3 text-sm leading-relaxed text-slate-300"><RouteIcon size={18} className="mt-0.5 shrink-0 text-brand-cyan" /> {event.route_info}</p>}
                {event.route_loops?.length > 0 && (
                  <div className="mt-6 grid gap-4 sm:grid-cols-3">
                    {event.route_loops.map((l) => (
                      <div key={l.name} data-testid={`route-loop-${l.name.split(" ")[0].toLowerCase()}`} className="card-k p-5">
                        <p className="font-display text-xl font-bold uppercase text-brand-cyan">{l.name}</p>
                        <p className="mt-2 text-xs leading-relaxed text-slate-400">{l.loop}</p>
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] tracking-wider text-slate-500">
                          {l.start && <span>START <span className="font-semibold text-brand-olive">{l.start}</span></span>}
                          {l.cutoff && <span>CUT-OFF <span className="font-semibold text-brand-olive">{l.cutoff}</span></span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {event.route_map && (
                  <div className="mt-6 overflow-hidden rounded-2xl border border-brand-line">
                    <img data-testid="route-map" src={event.route_map} alt={`${event.name} official route map with elevation profile`} loading="lazy" className="w-full bg-white object-contain" />
                    <p className="bg-brand-surface px-4 py-2.5 font-mono text-[10px] tracking-[0.15em] text-slate-500">OFFICIAL ROUTE MAP & ELEVATION PROFILE — HYDRATION / REFILL STATIONS MARKED · NO SINGLE-USE PLASTIC ON COURSE</p>
                  </div>
                )}
                {event.route_stats?.length > 0 && (
                  <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {event.route_stats.map((s) => (
                      <div key={s.k} data-testid={`route-stat-${s.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="card-k p-4 text-center">
                        <p className="font-display text-2xl font-bold text-brand-olive">{s.v}</p>
                        <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-slate-500">{s.k.toUpperCase()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}

            {event.how_to_reach?.length > 0 && (
              <Section title="How to Reach">
                <div className="space-y-0">
                  {event.how_to_reach.map((h, i) => (
                    <div key={h.t} data-testid={`reach-step-${i + 1}`} className="relative flex gap-5 border-l-2 border-brand-line pb-6 pl-5 last:pb-0">
                      <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-gradient-to-r from-brand-cyan to-brand-olive" />
                      <span className="w-8 shrink-0 font-mono text-sm font-semibold text-brand-cyan">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <p className="text-sm font-semibold text-white">{h.t}</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-400">{h.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {(event.stay_options?.length > 0 || event.travel_tips?.length > 0) && (
              <Section title="Stay & Travel Tips">
                <div className="grid gap-8 sm:grid-cols-2">
                  {event.stay_options?.length > 0 && (
                    <div>
                      <p className="mb-4 font-mono text-xs tracking-[0.2em] text-brand-olive">WHERE TO STAY</p>
                      <ul className="space-y-3">
                        {event.stay_options.map((t) => (
                          <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300"><BedDouble size={16} className="mt-0.5 shrink-0 text-brand-cyan" /> {t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {event.travel_tips?.length > 0 && (
                    <div>
                      <p className="mb-4 font-mono text-xs tracking-[0.2em] text-brand-cyan">TRAVEL TIPS</p>
                      <ul className="space-y-3">
                        {event.travel_tips.map((t) => (
                          <li key={t} className="flex items-start gap-3 text-sm leading-relaxed text-slate-300"><Lightbulb size={16} className="mt-0.5 shrink-0 text-brand-olive" /> {t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {event.schedule?.length > 0 && (
              <Section title="Event Schedule">
                <div className="space-y-0">
                  {event.schedule.map((s, i) => (
                    <div key={i} className="flex gap-5 border-l-2 border-brand-line pb-6 pl-5 last:pb-0 relative">
                      <span className="absolute -left-[7px] top-1 h-3 w-3 rounded-full bg-gradient-to-r from-brand-cyan to-brand-olive" />
                      <span className="w-16 shrink-0 font-mono text-sm font-semibold text-brand-cyan">{s.time}</span>
                      <span className="text-sm text-slate-300">{s.activity}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {event.benefits?.length > 0 && (
              <Section title="Participant Benefits">
                <p className="mb-5 font-mono text-xs tracking-[0.2em] text-brand-olive">MORE THAN A RACE KIT</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {event.benefits.map((b) => (
                    <div key={b} className="card-k flex items-center gap-3 px-4 py-3 text-sm text-slate-200">
                      <Award size={16} className="shrink-0 text-brand-olive" /> {b}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {event.training_plan?.length > 0 && (
              <Section title="Get Race-Ready">
                <p className="mb-5 font-mono text-xs tracking-[0.2em] text-brand-cyan">8-WEEK HIGH-ALTITUDE TRAINING PLAN</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {event.training_plan.map((w) => (
                    <div key={w.week} data-testid={`training-${w.week.toLowerCase()}`} className="card-k flex items-start gap-4 p-4">
                      <span className="shrink-0 rounded-lg border border-brand-olive/40 bg-brand-olive/10 px-2.5 py-1 font-mono text-xs font-bold text-brand-olive">{w.week}</span>
                      <div>
                        <p className="text-sm font-semibold text-white">{w.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-400">{w.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {event.training_plan_image && (
                  <a data-testid="training-plan-download" href={event.training_plan_image} target="_blank" rel="noreferrer" className="btn-kinetic btn-ghost-k mt-6">DOWNLOAD FULL PLAN</a>
                )}
              </Section>
            )}

            {event.prize_pool?.rows?.length > 0 && (
              <Section title="Awards & Prize Pool">
                <p className="mb-5 font-mono text-xs tracking-[0.2em] text-brand-olive">TOP 3 — MALE & FEMALE IN EACH CATEGORY</p>
                <div className="card-k overflow-x-auto" data-testid="awards-table">
                  <table className="w-full min-w-[560px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-brand-line font-mono text-[10px] tracking-[0.2em] text-slate-500">
                        <th className="px-5 py-3.5">CATEGORY</th>
                        <th className="px-5 py-3.5"><span className="inline-flex items-center gap-1.5"><Trophy size={13} className="text-amber-300" /> 1ST</span></th>
                        <th className="px-5 py-3.5"><span className="inline-flex items-center gap-1.5"><Medal size={13} className="text-slate-300" /> 2ND</span></th>
                        <th className="px-5 py-3.5"><span className="inline-flex items-center gap-1.5"><Medal size={13} className="text-orange-400" /> 3RD</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {event.prize_pool.rows.map((r) => (
                        <tr key={r.category} data-testid={`award-row-${r.category.replace(/\s+/g, "-").toLowerCase()}`} className="border-b border-brand-line/50 last:border-0">
                          <td className="px-5 py-4">
                            <span className="font-display text-lg font-bold uppercase text-white">{r.category}</span>
                            {r.gender && <span className="ml-2 font-mono text-[10px] tracking-widest text-slate-500">{r.gender.toUpperCase()}</span>}
                          </td>
                          <td className="px-5 py-4 font-semibold text-amber-300">{r.first}</td>
                          <td className="px-5 py-4 font-semibold text-slate-200">{r.second}</td>
                          <td className="px-5 py-4 font-semibold text-orange-400">{r.third}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {event.prize_pool.note && <p data-testid="awards-note" className="mt-4 font-mono text-[11px] tracking-[0.12em] text-slate-500">{event.prize_pool.note}</p>}
              </Section>
            )}

            <Section title="Race Essentials">
              <div className="grid gap-4 sm:grid-cols-2">
                {event.prizes && !event.prize_pool?.rows?.length && <div className="card-k p-5"><Trophy size={18} className="text-brand-cyan" /><p className="mt-3 text-sm font-semibold text-white">Awards</p><p className="mt-1 text-xs text-slate-400">{event.prizes}</p></div>}
                {event.tshirt && <div className="card-k p-5"><Shirt size={18} className="text-brand-cyan" /><p className="mt-3 text-sm font-semibold text-white">T-Shirt</p><p className="mt-1 text-xs text-slate-400">{event.tshirt}</p></div>}
                {event.medal && <div className="card-k p-5"><Medal size={18} className="text-brand-olive" /><p className="mt-3 text-sm font-semibold text-white">Medal</p><p className="mt-1 text-xs text-slate-400">{event.medal}</p></div>}
                {event.hydration && <div className="card-k p-5"><Droplets size={18} className="text-brand-olive" /><p className="mt-3 text-sm font-semibold text-white">Hydration</p><p className="mt-1 text-xs text-slate-400">{event.hydration}</p></div>}
                {event.refreshments && <div className="card-k p-5"><Coffee size={18} className="text-brand-cyan" /><p className="mt-3 text-sm font-semibold text-white">Refreshments</p><p className="mt-1 text-xs text-slate-400">{event.refreshments}</p></div>}
                {event.accommodation && <div className="card-k p-5"><BedDouble size={18} className="text-brand-olive" /><p className="mt-3 text-sm font-semibold text-white">Travel & Stay</p><p className="mt-1 text-xs text-slate-400">{event.accommodation}</p></div>}
              </div>
              {event.travel_info && <p className="mt-5 text-sm leading-relaxed text-slate-400">{event.travel_info}</p>}
            </Section>

            {event.rules?.length > 0 && (
              <Section title="Rules & Regulations">
                <ul className="space-y-2.5">
                  {event.rules.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-1 font-mono text-xs text-brand-cyan">{String(i + 1).padStart(2, "0")}</span> {r}
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {event.faqs?.length > 0 && (
              <Section title="FAQ">
                <Faq items={event.faqs} />
              </Section>
            )}

            {event.video && (
              <Section title="Event Film">
                <div className="overflow-hidden rounded-2xl border border-brand-line bg-black">
                  <video data-testid="event-video" src={event.video} controls playsInline preload="metadata" className="max-h-[480px] w-full object-contain" poster={event.image} />
                </div>
              </Section>
            )}

            {event.gallery?.length > 0 && (
              <Section title="Event Gallery">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {event.gallery.map((g, i) => {
                    const url = typeof g === "string" ? g : g.url;
                    const caption = typeof g === "string" ? "" : g.caption;
                    return (
                      <figure key={i} className="group relative overflow-hidden rounded-xl border border-brand-line">
                        <img data-testid={`event-gallery-${i}`} src={url} alt={caption || `${event.name} photo ${i + 1}`} loading="lazy"
                          className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                        {caption && <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-ink/95 to-transparent px-3 pb-2 pt-8 font-mono text-[9px] tracking-[0.15em] text-slate-300">{caption.toUpperCase()}</figcaption>}
                      </figure>
                    );
                  })}
                </div>
              </Section>
            )}

            {event.results?.length > 0 && (
              <Section title="Results">
                <p className="text-sm text-slate-400">Official results are available for this event.</p>
                <Link to="/results" data-testid="event-results-link" className="btn-kinetic btn-ghost-k mt-4">VIEW RESULTS & CERTIFICATES</Link>
              </Section>
            )}
          </div>

          <aside className="space-y-6">
            <div className="card-k sticky top-24 p-6">
              <p className="eyebrow">EVENT SNAPSHOT</p>
              <dl className="mt-5 space-y-3 text-sm">
                {[["Date", event.date_text], ["Venue", event.venue], ["State", event.state], ["Category", event.category], ["Distances", event.distances?.join(" | ")]].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-brand-line pb-2.5 last:border-0">
                    <dt className="text-slate-500">{k}</dt><dd className="text-right font-medium text-white">{v}</dd>
                  </div>
                ))}
              </dl>
              {canRegister && event.registration_mode === "onsite" && (
                <button data-testid="event-sidebar-register" onClick={() => setShowReg(true)} className="btn-kinetic btn-primary-k mt-6 w-full justify-center">REGISTER NOW</button>
              )}
              {event.contact && <p className="mt-4 flex items-center gap-2 text-xs text-slate-400"><Phone size={13} /> {event.contact}</p>}
              <div className="mt-5 grid gap-2">
                <button data-testid="event-volunteer-btn" onClick={() => setShowVol(true)} className="btn-kinetic btn-ghost-k w-full justify-center !py-2.5 !text-xs">VOLUNTEER FOR THIS EVENT</button>
                <Link to={`/partners?sponsor=${event.slug}`} data-testid="event-sponsor-btn" className="btn-kinetic w-full justify-center !py-2.5 !text-xs border border-brand-olive/40 text-brand-olive hover:bg-brand-olive/10 rounded-full font-semibold uppercase tracking-wider transition-colors inline-flex items-center gap-2 px-7">SPONSOR THIS EVENT</Link>
              </div>
              <p className="mt-4 text-xs text-slate-500">Questions? <Link to="/contact" className="text-brand-cyan hover:underline">Contact the SPORTIVE team</Link>.</p>
            </div>
          </aside>
        </div>
      </div>

      {showReg && <RegisterModal event={event} onClose={() => setShowReg(false)} />}
      {showVol && (
        <div data-testid="volunteer-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-brand-ink/80 backdrop-blur-sm" onClick={() => setShowVol(false)} />
          <div className="card-k relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto p-7">
            <button data-testid="volunteer-modal-close" onClick={() => setShowVol(false)} className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-white">✕</button>
            <p className="eyebrow">VOLUNTEER</p>
            <h3 className="mt-2 font-display text-3xl font-bold text-white">Join the Race Crew</h3>
            <div className="mt-6"><VolunteerForm event={event} onDone={() => setShowVol(false)} /></div>
          </div>
        </div>
      )}
    </div>
  );
}
